import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Select, message } from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type Notification,
  type NotificationFilters,
} from '@/api/notificationApi';
import { useAuth } from '@/hooks/useAuth';

const CHANNELS = ['email', 'in_app', 'sms', 'push'];
const STATUSES = ['pending', 'sending', 'sent', 'failed', 'read'];

export default function NotificationListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [channelFilter, setChannelFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const canRead = hasPermission('notification', 'read');
  const canWrite = hasPermission('notification', 'write');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: NotificationFilters = { ...params };
      if (channelFilter) filters.channel = channelFilter;
      if (statusFilter) filters.status = statusFilter;
      return getNotifications(filters);
    },
    [channelFilter, statusFilter]
  );

  const handleMarkRead = useCallback(
    async (id: string) => {
      try {
        await markNotificationRead(id);
        message.success(t('notification.marked_read'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const handleMarkAllRead = useCallback(async () => {
    try {
      const res = await markAllNotificationsRead();
      message.success(t('notification.marked_all_read', { count: res.marked_count }));
      gridRef.current?.reload();
    } catch {
      message.error(t('app.error'));
    }
  }, [t]);

  const statusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'blue';
      case 'read': return 'green';
      case 'failed': return 'red';
      case 'sending': return 'orange';
      default: return 'default';
    }
  };

  const columns: ColumnsType<Notification> = useMemo(
    () => [
      {
        title: t('notification.field.title'),
        dataIndex: 'title',
        key: 'title',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('notification.field.channel'),
        dataIndex: 'channel',
        key: 'channel',
        width: 100,
        render: (ch: string) => (
          <Tag>{t(`notification.channel.${ch}`, ch)}</Tag>
        ),
      },
      {
        title: t('notification.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (st: string) => (
          <Tag color={statusColor(st)}>{t(`notification.status.${st}`, st)}</Tag>
        ),
      },
      {
        title: t('notification.field.event_type'),
        dataIndex: 'event_type',
        key: 'event_type',
        width: 150,
        ellipsis: true,
      },
      {
        title: t('notification.field.sent_at'),
        dataIndex: 'sent_at',
        key: 'sent_at',
        width: 170,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 100,
        render: (_: unknown, record: Notification) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/notifications/${record.id}`)}
            />
            {canWrite && record.status === 'sent' && (
              <Button
                type="link"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleMarkRead(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, handleMarkRead]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('notification.field.channel')}
        value={channelFilter}
        onChange={(v) => { setChannelFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={CHANNELS.map((ch) => ({
          value: ch,
          label: t(`notification.channel.${ch}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('notification.field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={STATUSES.map((st) => ({
          value: st,
          label: t(`notification.status.${st}`),
        }))}
      />
      {canWrite && (
        <Button
          icon={<CheckCircleOutlined />}
          onClick={handleMarkAllRead}
        >
          {t('notification.mark_all_read')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return null;

  return (
    <div>
      <h2>{t('notification.notifications_title')}</h2>
      <DataGrid<Notification>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('notification.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
